/**
 * AES-GCM encryption/decryption utilities.
 * Uses Web Crypto API for secure cryptographic operations.
 */

/**
 * Encrypt plaintext using AES-GCM with the provided master key.
 * Returns base64-encoded ciphertext with prepended IV.
 * 
 * @param plaintext - String to encrypt
 * @param masterKey - 32-byte encryption key
 * @returns Base64-encoded string containing IV + ciphertext
 */
export async function encrypt(plaintext: string, masterKey: Uint8Array): Promise<string> {
  // Generate random 12-byte IV (recommended for AES-GCM)
  const iv = crypto.getRandomValues(new Uint8Array(12));
  
  // Import the master key for AES-GCM
  const key = await crypto.subtle.importKey(
    'raw',
    masterKey,
    { name: 'AES-GCM' },
    false,
    ['encrypt']
  );
  
  // Encrypt the plaintext
  const encodedPlaintext = new TextEncoder().encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encodedPlaintext
  );
  
  // Combine IV + ciphertext into a single array
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);
  
  // Return as base64
  let binary = '';
  const len = combined.length;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(combined[i]);
  }
  return btoa(binary);
}

/**
 * Decrypt ciphertext using AES-GCM with the provided master key.
 * Expects base64-encoded string containing IV + ciphertext.
 * 
 * @param ciphertext - Base64-encoded string from encrypt()
 * @param masterKey - 32-byte encryption key
 * @returns Decrypted plaintext string
 */
export async function decrypt(ciphertext: string, masterKey: Uint8Array): Promise<string> {
  // Decode base64 to bytes
  const combined = Uint8Array.from(atob(ciphertext), c => c.charCodeAt(0));
  
  // Extract IV (first 12 bytes) and ciphertext (rest)
  const iv = combined.slice(0, 12);
  const data = combined.slice(12);
  
  // Import the master key for AES-GCM
  const key = await crypto.subtle.importKey(
    'raw',
    masterKey,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );
  
  // Decrypt
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    data
  );
  
  return new TextDecoder().decode(plaintext);
}

/**
 * Encrypt an object by JSON-stringifying it first.
 */
export async function encryptObject<T>(obj: T, masterKey: Uint8Array): Promise<string> {
  return encrypt(JSON.stringify(obj), masterKey);
}

/**
 * Decrypt and parse a JSON object.
 */
export async function decryptObject<T>(ciphertext: string, masterKey: Uint8Array): Promise<T> {
  const plaintext = await decrypt(ciphertext, masterKey);
  return JSON.parse(plaintext) as T;
}
